import { concat } from '@ember/helper';
import Component from '@glimmer/component';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import { eq, or } from 'ember-truth-helpers';
import dateFormat from 'garaje/helpers/date-format';
import type Entry from 'garaje/models/entry';
import type ScreenerReview from 'garaje/models/screener-review';
import { reads } from 'macro-decorators';

interface IdScanningColumnSignature {
  Args: {
    entry: Entry;
  };
  Element: HTMLDivElement;
}

export default class IdScanningColumn extends Component<IdScanningColumnSignature> {
  @reads('args.entry.currentApprovalStatus') status!: string;

  @reads('review.reviewerName') reviewerName!: string;
  @reads('review.reviewedAt') reviewedAt!: string | Date;

  get review(): ScreenerReview | undefined {
    return this.args.entry.visitApproval?.screenerReviews?.slice().at(-1);
  }

  get didFailIdScanningCheck(): boolean {
    return Boolean(this.args.entry.visitApproval?.idScanningResult?.didFail);
  }

  get hasReports(): boolean {
    return Boolean(this.args.entry.visitApproval?.idScanningResult);
  }

  <template>
    <div
      class="table-cell border-b align-top px-2 pt-6 text-carbon-50 whitespace-nowrap"
      data-test-id-scanning-status
      data-test-id-scanning-value={{this.status}}
      ...attributes
    >
      {{#if this.hasReports}}
        {{#if (eq this.status "pending")}}
          <img src="/assets/images/progress-small-gray.svg" alt="" role="presentation" />&nbsp;
          <span class="whitespace-nowrap" data-test-pending>
            ID processing
          </span>
        {{else if (eq this.status "denied")}}
          <EmberTooltip @side="top">
            By
            {{or this.reviewerName "system"}}
            {{if this.reviewedAt (concat "on " (dateFormat this.reviewedAt "MMMM D, YYYY [at] h:mm a"))}}
          </EmberTooltip>
          <img
            src="/assets/images/cross-small-red.svg"
            alt=""
            role="presentation"
            style="width:10px; height:10px;"
          />&nbsp;
          <span class="whitespace-nowrap" data-test-denied>
            Access denied
          </span>
        {{else if (eq this.status "approved")}}
          {{#if this.didFailIdScanningCheck}}
            <EmberTooltip @side="top">
              By
              {{or this.reviewerName "system"}}
              {{if this.reviewedAt (concat "on " (dateFormat this.reviewedAt "MMMM D, YYYY [at] h:mm a"))}}
            </EmberTooltip>
            <img
              src="/assets/images/checkmark-small-gray.svg"
              alt=""
              role="presentation"
              style="width:11px; height:10px;"
            />&nbsp;
            <span class="whitespace-nowrap" data-test-approved>
              Access approved
            </span>
          {{else}}
            <img
              src="/assets/images/checkmark-small-gray.svg"
              alt=""
              role="presentation"
              style="width:11px; height:10px;"
            />&nbsp;
            <span class="whitespace-nowrap" data-test-valid>
              ID accepted
            </span>
          {{/if}}
        {{/if}}
      {{else}}
        <img src="/assets/images/gray-dash-small.svg" alt="" role="presentation" />&nbsp;
        <span class="whitespace-nowrap" data-test-not-required>
          Not required
        </span>
      {{/if}}
    </div>
  </template>
}
